/**
 * Tonight — the glanceable home (design/project/lib/dash.jsx Tonight +
 * dash-desktop.jsx DOverview, one responsive DOM). Greeting + date,
 * glance strip, phase-6 morning-review digest above "Working now",
 * working-now live card with live tool ticker, tonight's run order, and
 * a desktop-only recent-sessions rail. First-run renders the teaching
 * three-step flow instead.
 */
import { Link } from "react-router-dom";
import type { Job, Machine, Schedule, Session } from "../lib/api";
import { timeAgo, tokenSummary } from "../lib/format";
import { Card, StatusDot, StatusPill, SwarmMoon, Btn, Icon, I } from "../kit";
import { SecLabel } from "./DashShell";
import { ReviewSection } from "./ReviewSection";
import { BudgetCard } from "./BudgetCard";
import { parseCron, cronTime } from "./ScheduleEditor";

const DOWNLOAD_HREF = "/downloads/NightSwarm-mac-arm64-latest.dmg";

function greeting(now: Date): string {
  const h = now.getHours();
  if (h >= 17 || h < 4) return "Good evening";
  if (h < 12) return "Good morning";
  return "Good afternoon";
}

function kicker(now: Date): string {
  const day = now.toLocaleDateString([], { weekday: "long" });
  const md = now.toLocaleDateString([], { month: "short", day: "numeric" });
  const time = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${day} · ${md} · ${time}`;
}

function elapsed(startedAt: string): string {
  const s = Math.max(0, (Date.now() - new Date(startedAt).getTime()) / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/** Overnight ordering: evening hours first, then the early morning. */
function runOrderKey(cron: string): number {
  const m = parseCron(cron);
  if (!m) return Number.MAX_SAFE_INTEGER;
  const h = m.hour < 12 ? m.hour + 24 : m.hour;
  return h * 60 + m.min;
}

const LAST_NIGHT_MS = 18 * 3600 * 1000;

export function TonightTab({
  userEmail,
  machines,
  sessions,
  schedules,
  jobs,
  working,
  ticker,
  firstRun,
  machineName,
  onNewAgent,
  onNewSchedule,
}: {
  userEmail?: string;
  machines: Machine[] | null;
  sessions: Session[] | null;
  schedules: Schedule[] | null;
  jobs: Job[] | null;
  /** The most recent live (starting/running) session, when any. */
  working: Session | null;
  /** Latest tool/assistant activity line for the working session. */
  ticker: string | null;
  firstRun: boolean;
  machineName: (machineId: string) => string;
  onNewAgent: () => void;
  onNewSchedule: () => void;
}) {
  if (firstRun) {
    return (
      <FirstRunHome onNewAgent={onNewAgent} onNewSchedule={onNewSchedule} />
    );
  }

  const now = new Date();
  const name = userEmail ? userEmail.split("@")[0] : "there";

  const online = (machines ?? []).filter((m) => m.status === "online").length;
  const workingCount = (sessions ?? []).filter(
    (s) => s.status === "running" || s.status === "starting"
  ).length;
  const enabledSchedules = (schedules ?? []).filter((s) => s.enabled);
  const queuedTonight = enabledSchedules.length;

  // Last-night stat (desktop 4th glance card) — real job outcomes from
  // the last 18h, the same window the review digest uses plane-side.
  const cutoff = Date.now() - LAST_NIGHT_MS;
  const recentJobs = (jobs ?? []).filter(
    (j) => j.createdAt && new Date(j.createdAt).getTime() >= cutoff
  );
  const deliveredCount = recentJobs.filter((j) => j.state === "delivered").length;
  const failedCount = recentJobs.filter((j) => j.state === "failed").length;

  const runOrder = [...enabledSchedules].sort(
    (a, b) => runOrderKey(a.cron) - runOrderKey(b.cron)
  );

  const recentSessions = (sessions ?? []).slice(0, 5);

  return (
    <div className="dsh-stack">
      {/* greeting */}
      <div className="dsh-greet ns-reveal">
        <div className="dsh-kicker">{kicker(now)}</div>
        <h2>
          {greeting(now)}, {name}.
        </h2>
        <p>
          The swarm is on watch.{" "}
          <strong>
            {queuedTonight} schedule{queuedTonight === 1 ? "" : "s"}
          </strong>{" "}
          {queuedTonight === 1 ? "is" : "are"} queued for tonight.
        </p>
      </div>

      {/* glance strip */}
      <div className="dsh-glance">
        <Card pad={12}>
          <div className="dsh-stat-head">
            <StatusDot tone={online > 0 ? "live" : "off"} size={6} />
            <span>machines</span>
          </div>
          <div className="dsh-stat-value">
            <strong>{machines === null ? "–" : online}</strong>
            <span>online</span>
          </div>
        </Card>
        <Card pad={12}>
          <div className="dsh-stat-head">
            <StatusDot
              tone={workingCount > 0 ? "live" : "idle"}
              pulse={workingCount > 0}
              size={6}
            />
            <span>working</span>
          </div>
          <div className="dsh-stat-value">
            <strong>{sessions === null ? "–" : workingCount}</strong>
            <span>now</span>
          </div>
        </Card>
        <Card pad={12}>
          <div className="dsh-stat-head">
            <StatusDot
              tone={queuedTonight > 0 ? "pending" : "idle"}
              size={6}
            />
            <span>queued</span>
          </div>
          <div className="dsh-stat-value">
            <strong>{schedules === null ? "–" : queuedTonight}</strong>
            <span>tonight</span>
          </div>
        </Card>
        <Card pad={12} className="dsh-stat--desktop">
          <div className="dsh-stat-head">
            <StatusDot
              tone={failedCount > 0 ? "warn" : deliveredCount > 0 ? "live" : "idle"}
              size={6}
            />
            <span>last night</span>
          </div>
          <div className="dsh-stat-value">
            <strong>{jobs === null ? "–" : deliveredCount}</strong>
            <span>
              delivered{failedCount > 0 ? ` · ${failedCount} failed` : ""}
            </span>
          </div>
        </Card>
      </div>

      {/* phase 6 §3: unreviewed overnight digest, above Working now */}
      <ReviewSection userEmail={userEmail} machineName={machineName} />

      <div className="dsh-tonight-grid">
        <div className="dsh-tonight-col">
          {/* working now — live */}
          <div>
            <SecLabel
              action={
                <Link className="dsh-seclabel-action" to="/dashboard/activity">
                  view all
                </Link>
              }
            >
              Working now
            </SecLabel>
            {working ? (
              <Link
                to={`/sessions/${working.sessionId}`}
                className="ns-card dsh-live-card"
                style={{ padding: 16 }}
              >
                <div className="dsh-live-head">
                  <StatusDot tone="live" pulse size={8} />
                  <span className="agent">{working.agent}</span>
                  <span className="right">
                    <StatusPill
                      status={
                        working.status === "running" ? "streaming" : "queued"
                      }
                    >
                      {working.status === "running" ? "live" : "starting"}
                    </StatusPill>
                  </span>
                </div>
                {working.cwd && (
                  <div className="dsh-live-title">{working.cwd}</div>
                )}
                <div className="dsh-ticker">
                  <Icon d={I.wrench} size={12} />
                  {ticker ?? "working…"}
                </div>
                <div className="dsh-live-foot">
                  <span>
                    {machineName(working.machineId)} ·{" "}
                    {elapsed(working.startedAt)} elapsed
                    {tokenSummary(working)
                      ? ` · ${tokenSummary(working)}`
                      : ""}
                  </span>
                  <span className="go">open session →</span>
                </div>
              </Link>
            ) : (
              <Card>
                <div className="dsh-live-head">
                  <StatusDot tone="idle" size={8} />
                  <span
                    className="mono"
                    style={{ fontSize: 13, color: "var(--color-fg-3)" }}
                  >
                    nothing running right now
                  </span>
                </div>
                <p
                  className="hint"
                  style={{ marginTop: 10, marginBottom: 0 }}
                >
                  The swarm is quiet. Queued schedules fire overnight, or run
                  a job now from Activity.
                </p>
              </Card>
            )}
          </div>

          {/* tonight's run order */}
          <div>
            <SecLabel
              action={
                <Link
                  className="dsh-seclabel-action"
                  to="/dashboard/schedules"
                >
                  edit
                </Link>
              }
            >
              Tonight's run order
            </SecLabel>
            {runOrder.length === 0 ? (
              <Card>
                <p className="hint" style={{ margin: 0 }}>
                  No schedules enabled — the swarm rests tonight.{" "}
                  <button
                    type="button"
                    className="dsh-seclabel-action"
                    onClick={onNewSchedule}
                  >
                    + new schedule
                  </button>
                </p>
              </Card>
            ) : (
              <Card pad={0}>
                {runOrder.map((s) => (
                  <div className="dsh-runorder-row" key={s.scheduleId}>
                    <span className="time" title={s.cron}>
                      {cronTime(s.cron) ?? "—"}
                    </span>
                    <span className="rule" />
                    <div className="what">
                      <div className="name">{s.name}</div>
                      <div className="agent">{s.agentName}</div>
                    </div>
                    <StatusPill status="queued" />
                  </div>
                ))}
              </Card>
            )}
          </div>
        </div>

        {/* desktop right rail: recent sessions */}
        <div className="dsh-tonight-col dsh-desktop-only">
          <div>
            <SecLabel
              action={
                <Link className="dsh-seclabel-action" to="/dashboard/activity">
                  view all
                </Link>
              }
            >
              Recent sessions
            </SecLabel>
            {recentSessions.length === 0 ? (
              <Card>
                <p className="hint" style={{ margin: 0 }}>
                  No sessions yet — they'll appear here when work runs.
                </p>
              </Card>
            ) : (
              <Card pad={0}>
                {recentSessions.map((s) => {
                  const live =
                    s.status === "running" || s.status === "starting";
                  // needs-human dominates the row's signal: a session waiting
                  // on a human must be visibly distinct from one actively
                  // working, not a plain green "live" dot (2026-06-11 fix).
                  const needs = !!s.needsHuman && s.status !== "ended";
                  return (
                    <Link
                      key={s.sessionId}
                      to={`/sessions/${s.sessionId}`}
                      className={`dsh-mini-row${needs ? " needs" : ""}`}
                    >
                      <StatusDot
                        tone={
                          needs
                            ? "warn"
                            : live
                              ? "live"
                              : s.status === "error"
                                ? "danger"
                                : "off"
                        }
                        pulse={needs || live}
                        size={7}
                      />
                      <div className="what">
                        <div className="name">{s.agent}</div>
                        <div className="sub">
                          {machineName(s.machineId)} ·{" "}
                          {needs ? "needs you" : s.status}
                        </div>
                      </div>
                      <span
                        className={`when${needs ? " needs" : live ? " live" : ""}`}
                      >
                        {needs
                          ? "waiting"
                          : live
                            ? `live · ${elapsed(s.startedAt)}`
                            : timeAgo(s.startedAt)}
                      </span>
                    </Link>
                  );
                })}
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* phase 9 §B/§D: budget surface — Tonight tab footer. Hidden by the
          card itself when the plane has no /settings route yet. */}
      <BudgetCard sessions={sessions} machines={machines} />
    </div>
  );
}

/** Teaching first-run flow (Dashboard.html FirstRunHome). */
function FirstRunHome({
  onNewAgent,
  onNewSchedule,
}: {
  onNewAgent: () => void;
  onNewSchedule: () => void;
}) {
  return (
    <div className="dsh-stack dsh-firstrun">
      <div className="dsh-firstrun-hero">
        <SwarmMoon size={56} animate />
        <h2>Your swarm is empty.</h2>
        <p>Three steps to a working night shift. Takes about two minutes.</p>
      </div>
      <div className="dsh-stack" style={{ gap: 12 }}>
        <Card className="dsh-step">
          <div className="dsh-step-rail">
            <span className="dsh-step-num">1</span>
            <span className="dsh-step-line" />
          </div>
          <div className="dsh-step-body">
            <div className="dsh-step-title">
              <Icon d={I.cpu} size={16} /> Enroll your first Mac
            </div>
            <p>
              Download the .dmg and drag NightSwarm into Applications. It
              registers the machine and keeps a heartbeat. Signed +
              notarized — nothing else to install.
            </p>
            <a className="ns-btn ns-btn--primary" href={DOWNLOAD_HREF}>
              <Icon d={I.download} size={14} /> Download menubar app
            </a>
          </div>
        </Card>
        <Card className="dsh-step">
          <div className="dsh-step-rail">
            <span className="dsh-step-num">2</span>
            <span className="dsh-step-line" />
          </div>
          <div className="dsh-step-body">
            <div className="dsh-step-title">
              <Icon d={I.bot} size={16} /> Stand up an agent
            </div>
            <p>
              A named, persistent Claude Code workspace pinned to that Mac (or
              any). Message it and it routes to a live session.
            </p>
            <Btn onClick={onNewAgent}>Create agent</Btn>
          </div>
        </Card>
        <Card className="dsh-step">
          <div className="dsh-step-rail">
            <span className="dsh-step-num">3</span>
          </div>
          <div className="dsh-step-body">
            <div className="dsh-step-title">
              <Icon d={I.clock} size={16} /> Schedule the night shift
            </div>
            <p>Give it overnight work. Wake to the result.</p>
            <Btn onClick={onNewSchedule}>New schedule</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
}
